// Lightweight string-table i18n. Two locales (en, fr), browser auto-detect,
// optional localStorage override. Locale changes via setLocale() reload the
// page so every component picks up the new strings without context plumbing.

export type Locale = 'en' | 'fr';

const STORAGE_KEY = 'charzapp_locale';

function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'fr') return stored;
  const nav = (window.navigator.language || 'en').toLowerCase();
  return nav.startsWith('fr') ? 'fr' : 'en';
}

let currentLocale: Locale = detectLocale();

export function getLocale(): Locale { return currentLocale; }

export function setLocale(next: Locale): void {
  if (next === currentLocale) return;
  window.localStorage.setItem(STORAGE_KEY, next);
  currentLocale = next;
  window.location.reload();
}

type Strings = Record<string, string>;

const en: Strings = {
  'app.loading': 'Loading…',
  'app.never': 'never',
  'time.seconds_ago': '{n}s ago',
  'time.minutes_ago': '{n} min ago',
  'time.hours_ago': '{n} h ago',
  'time.days_ago': '{n} d ago',

  'header.updated': 'updated {ago}',
  'header.calls_left': '{n} API calls left',
  'header.calls_left_unknown': '? API calls left',
  'header.refresh': 'Get new data',
  'header.refresh_title': 'Fetch fresh data from Tado',
  'header.refresh_disabled': 'Quota too low',
  'header.settings': 'Settings',

  'hero.room': 'Living Room',
  'hero.target': 'target',
  'hero.until': 'until',
  'hero.end_override': 'End override',
  'hero.heating_label': 'Heating · {n}%',

  'tile.humidity': 'Humidity',
  'tile.outside': 'Outside',
  'tile.home': 'Home',
  'tile.presence_home': 'Yes',
  'tile.presence_away': 'Away',
  'tile.tap_to_change': 'tap to change',

  'action.set_temp': 'Set temperature',

  'set_temp.title': 'Set target',
  'set_temp.help': 'half-degree steps · {min}–{max} °C',
  'set_temp.until_next_change': 'Until next change',
  'set_temp.for_1h': 'For 1h',
  'set_temp.for_2h': 'For 2h',
  'set_temp.for_4h': 'For 4h',
  'set_temp.manual': 'Manual',
  'set_temp.cta': 'Set temperature',
  'set_temp.cta_busy': 'Setting…',
  'set_temp.failed': 'Failed to set temperature',
  'set_temp.dialog_label': 'Set target temperature',
  'set_temp.decrease': 'Decrease',
  'set_temp.increase': 'Increase',

  'presence.title': 'Presence',
  'presence.home': 'Home',
  'presence.away': 'Away',
  'presence.auto': 'Auto',
  'presence.forced': 'forced',
  'presence.geofence': 'geofence',
  'presence.apply': 'Apply',
  'presence.applying': 'Applying…',
  'presence.failed': 'Failed to update presence',

  'settings.title': 'Settings',
  'settings.recent_actions': 'Recent actions',
  'settings.api_quota': 'API quota',
  'settings.calls_left': '{n} calls left',
  'settings.calls_left_unknown': '? calls left',
  'settings.sign_out': 'Sign out',
  'settings.back': '‹ Back',
  'settings.language': 'Language',
  'settings.lang_english': 'English',
  'settings.lang_french': 'Français',
  'settings.col_when': 'When',
  'settings.col_action': 'Action',
  'settings.col_result': 'Result',

  'login.password': 'Password',
  'login.sign_in': 'Sign in',
  'login.signing_in': 'Signing in…',
  'login.wrong_password': 'Wrong password',
  'login.failed': 'Login failed',

  'setup.title': 'Setup',
  'setup.choose_password': 'Choose a shared password for your household.',
  'setup.password_min': 'Password (min 8)',
  'setup.confirm': 'Confirm',
  'setup.continue': 'Continue',
  'setup.connect_tado': 'Connect your Tado account.',
  'setup.start_tado': 'Start Tado authorization',
  'setup.open_url': '1. Open',
  'setup.confirm_code': '2. Confirm code:',
  'setup.waiting': 'Waiting for confirmation…',
  'setup.finishing': 'Done, finishing up…',
  'setup.complete': 'Setup complete! Loading dashboard…',
  'setup.error_min_length': 'Password must be at least 8 chars',
  'setup.error_mismatch': 'Passwords do not match',
  'setup.error_set': 'Could not set password (already set?)',
  'setup.error_tado': 'Could not start Tado authorization',

  'chart.title': 'History',
  'chart.indoor': 'Indoor °C',
  'chart.target': 'Target °C',
  'chart.outside': 'Outside °C',
  'chart.heating': 'Heating %',
  'chart.range_24h': '24h',
  'chart.range_7d': '7d',
  'chart.range_30d': '30d',
  'chart.range_all': 'All',

  'nav.temperature': 'Temperature',
  'nav.video': 'Video',
  'nav.infos': 'Info',
  'nav.memories': 'Memories',
  'nav.runtimes': 'Usage',
  'nav.settings': 'Settings',

  'runtimes.tile_title': 'Heating runtime',
  'runtimes.col_week': 'Week',
  'runtimes.col_month': 'Month',
  'runtimes.col_year': 'Year',
  'runtimes.hours': '{n} h',
  'runtimes.no_config_hint': 'Set heat pump power in Settings to see kWh.',

  'runtimes.title': 'Heating usage',
  'runtimes.bucket_day': 'Day',
  'runtimes.bucket_week': 'Week',
  'runtimes.bucket_month': 'Month',
  'runtimes.bucket_year': 'Year',
  'runtimes.range_12m': '12 months',
  'runtimes.range_5y': '5 years',
  'runtimes.range_all': 'All',
  'runtimes.refresh': 'Refresh from Tado',
  'runtimes.refreshing': 'Refreshing…',
  'runtimes.refresh_failed': 'Refresh failed',
  'runtimes.empty': 'No data yet. Try refreshing from Tado.',
  'runtimes.axis_hours': 'hours',

  'settings.energy': 'Heat pump & tariff',
  'settings.energy_power': 'Heat pump power (kW)',
  'settings.energy_tariff': 'Electricity tariff (€ / kWh)',
  'settings.energy_save': 'Save',
  'settings.energy_saved': 'Saved',
  'settings.energy_invalid': 'Use positive numbers (or leave empty).',

  'video.loading': 'Loading cameras…',
  'video.error': 'Could not load cameras. Try again later.',
  'video.refresh': 'Refresh',
  'video.not_connected_title': 'Cameras not connected',
  'video.not_connected_body': 'Connect your Netatmo account in Settings to see your cameras here.',

  'camera.monitoring_on': 'Monitoring on',
  'camera.monitoring_off': 'Monitoring off',
  'camera.monitoring_off_notice': 'Monitoring off — no image',
  'camera.offline': 'Camera offline',
  'camera.floodlight': 'Floodlight',
  'camera.floodlight_auto': 'Auto',
  'camera.floodlight_on': 'On',
  'camera.floodlight_off': 'Off',

  'settings.camera': 'Camera',
  'settings.camera_connect': 'Connect',
  'settings.camera_disconnect': 'Disconnect',
  'settings.camera_not_configured': 'Not configured',
  'settings.camera_error': 'Could not reach the camera service. Try again.',

  'infos.title': 'Info',
  'infos.rules_title': 'House document',
  'infos.rules_open': 'Open the document',
  'infos.rules_scan': 'Scan to open on your phone',
  'infos.rules_unset': 'No house document configured yet. Add a link in Settings.',
  'infos.telescope_title': 'Telescope',
  'infos.telescope_open': 'Open the document',
  'infos.telescope_unset': 'No telescope document configured yet. Add a link in Settings.',

  'memories.title': 'Memories',
  'memories.add_photos': 'Add photos',
  'memories.uploading': 'Uploading…',
  'memories.empty': 'No photos yet. Be the first to add one!',
  'memories.delete': 'Delete',
  'memories.delete_confirm': 'Delete this photo?',
  'memories.close': 'Close',
  'memories.upload_error': 'Photos could not be uploaded. Please try again.',
  'memories.upload_failed': 'Some photos could not be added: {names}',

  'settings.house_rules': 'House document',
  'settings.telescope': 'Telescope',
  'settings.doc_placeholder': 'https://docs.google.com/…',
  'settings.doc_save': 'Save',
  'settings.doc_saved': 'Saved',
  'settings.doc_invalid': 'Enter a valid URL',
};

const fr: Strings = {
  'app.loading': 'Chargement…',
  'app.never': 'jamais',
  'time.seconds_ago': 'il y a {n}s',
  'time.minutes_ago': 'il y a {n} min',
  'time.hours_ago': 'il y a {n} h',
  'time.days_ago': 'il y a {n} j',

  'header.updated': 'mis à jour {ago}',
  'header.calls_left': '{n} appels API restants',
  'header.calls_left_unknown': '? appels API restants',
  'header.refresh': 'Actualiser',
  'header.refresh_title': 'Récupérer les données fraîches depuis Tado',
  'header.refresh_disabled': 'Quota trop bas',
  'header.settings': 'Paramètres',

  'hero.room': 'Salon',
  'hero.target': 'cible',
  'hero.until': "jusqu'à",
  'hero.end_override': "Annuler l'écart",
  'hero.heating_label': 'Chauffage · {n}%',

  'tile.humidity': 'Humidité',
  'tile.outside': 'Extérieur',
  'tile.home': 'Maison',
  'tile.presence_home': 'Oui',
  'tile.presence_away': 'Absent',
  'tile.tap_to_change': 'changer',

  'action.set_temp': 'Régler la température',

  'set_temp.title': 'Définir la cible',
  'set_temp.help': 'pas de 0,5° · {min}–{max} °C',
  'set_temp.until_next_change': 'Jusqu’au prochain changement',
  'set_temp.for_1h': 'Pour 1h',
  'set_temp.for_2h': 'Pour 2h',
  'set_temp.for_4h': 'Pour 4h',
  'set_temp.manual': 'Manuel',
  'set_temp.cta': 'Régler la température',
  'set_temp.cta_busy': 'Réglage…',
  'set_temp.failed': 'Échec du réglage',
  'set_temp.dialog_label': 'Régler la température cible',
  'set_temp.decrease': 'Diminuer',
  'set_temp.increase': 'Augmenter',

  'presence.title': 'Présence',
  'presence.home': 'Maison',
  'presence.away': 'Absent',
  'presence.auto': 'Auto',
  'presence.forced': 'forcé',
  'presence.geofence': 'géo',
  'presence.apply': 'Appliquer',
  'presence.applying': 'Application…',
  'presence.failed': 'Échec de mise à jour',

  'settings.title': 'Paramètres',
  'settings.recent_actions': 'Activité récente',
  'settings.api_quota': 'Quota API',
  'settings.calls_left': '{n} appels restants',
  'settings.calls_left_unknown': '? appels restants',
  'settings.sign_out': 'Se déconnecter',
  'settings.back': '‹ Retour',
  'settings.language': 'Langue',
  'settings.lang_english': 'English',
  'settings.lang_french': 'Français',
  'settings.col_when': 'Quand',
  'settings.col_action': 'Action',
  'settings.col_result': 'Résultat',

  'login.password': 'Mot de passe',
  'login.sign_in': 'Se connecter',
  'login.signing_in': 'Connexion…',
  'login.wrong_password': 'Mot de passe incorrect',
  'login.failed': 'Échec de la connexion',

  'setup.title': 'Configuration',
  'setup.choose_password': 'Choisissez un mot de passe partagé pour votre foyer.',
  'setup.password_min': 'Mot de passe (min. 8)',
  'setup.confirm': 'Confirmer',
  'setup.continue': 'Continuer',
  'setup.connect_tado': 'Connectez votre compte Tado.',
  'setup.start_tado': 'Démarrer la connexion Tado',
  'setup.open_url': '1. Ouvrez',
  'setup.confirm_code': '2. Code de confirmation :',
  'setup.waiting': 'En attente de confirmation…',
  'setup.finishing': 'Finalisation…',
  'setup.complete': 'Configuration terminée ! Chargement du tableau de bord…',
  'setup.error_min_length': 'Le mot de passe doit contenir au moins 8 caractères',
  'setup.error_mismatch': 'Les mots de passe ne correspondent pas',
  'setup.error_set': 'Impossible de définir le mot de passe (déjà défini ?)',
  'setup.error_tado': 'Impossible de démarrer la connexion Tado',

  'chart.title': 'Historique',
  'chart.indoor': 'Intérieur °C',
  'chart.target': 'Cible °C',
  'chart.outside': 'Extérieur °C',
  'chart.heating': 'Chauffage %',
  'chart.range_24h': '24h',
  'chart.range_7d': '7j',
  'chart.range_30d': '30j',
  'chart.range_all': 'Tout',

  'nav.temperature': 'Température',
  'nav.video': 'Vidéo',
  'nav.infos': 'Infos',
  'nav.memories': 'Souvenirs',
  'nav.runtimes': 'Conso',
  'nav.settings': 'Réglages',

  'runtimes.tile_title': 'Heures de chauffe',
  'runtimes.col_week': 'Semaine',
  'runtimes.col_month': 'Mois',
  'runtimes.col_year': 'Année',
  'runtimes.hours': '{n} h',
  'runtimes.no_config_hint': 'Renseignez la puissance de la PAC dans les Réglages pour voir les kWh.',

  'runtimes.title': 'Consommation chauffage',
  'runtimes.bucket_day': 'Jour',
  'runtimes.bucket_week': 'Semaine',
  'runtimes.bucket_month': 'Mois',
  'runtimes.bucket_year': 'Année',
  'runtimes.range_12m': '12 mois',
  'runtimes.range_5y': '5 ans',
  'runtimes.range_all': 'Tout',
  'runtimes.refresh': 'Rafraîchir depuis Tado',
  'runtimes.refreshing': 'Rafraîchissement…',
  'runtimes.refresh_failed': 'Échec du rafraîchissement',
  'runtimes.empty': 'Aucune donnée. Essayez de rafraîchir depuis Tado.',
  'runtimes.axis_hours': 'heures',

  'settings.energy': 'PAC & tarif',
  'settings.energy_power': 'Puissance PAC (kW)',
  'settings.energy_tariff': 'Tarif électricité (€ / kWh)',
  'settings.energy_save': 'Enregistrer',
  'settings.energy_saved': 'Enregistré',
  'settings.energy_invalid': 'Saisissez des nombres positifs (ou laissez vide).',

  'video.loading': 'Chargement des caméras…',
  'video.error': 'Impossible de charger les caméras. Réessayez plus tard.',
  'video.refresh': 'Actualiser',
  'video.not_connected_title': 'Caméras non connectées',
  'video.not_connected_body': 'Connectez votre compte Netatmo dans les Réglages pour voir vos caméras ici.',

  'camera.monitoring_on': 'Surveillance activée',
  'camera.monitoring_off': 'Surveillance désactivée',
  'camera.monitoring_off_notice': "Surveillance désactivée — pas d'image",
  'camera.offline': 'Caméra hors ligne',
  'camera.floodlight': 'Projecteur',
  'camera.floodlight_auto': 'Auto',
  'camera.floodlight_on': 'Allumé',
  'camera.floodlight_off': 'Éteint',

  'settings.camera': 'Caméra',
  'settings.camera_connect': 'Connecter',
  'settings.camera_disconnect': 'Déconnecter',
  'settings.camera_not_configured': 'Non configuré',
  'settings.camera_error': 'Service caméra injoignable. Réessayez.',

  'infos.title': 'Infos',
  'infos.rules_title': 'Document de la maison',
  'infos.rules_open': 'Ouvrir le document',
  'infos.rules_scan': 'Scannez pour ouvrir sur votre téléphone',
  'infos.rules_unset': 'Aucun document de la maison configuré. Ajoutez un lien dans les Réglages.',
  'infos.telescope_title': 'Téléscope',
  'infos.telescope_open': 'Ouvrir le document',
  'infos.telescope_unset': 'Aucun document téléscope configuré. Ajoutez un lien dans les Réglages.',

  'memories.title': 'Souvenirs',
  'memories.add_photos': 'Ajouter des photos',
  'memories.uploading': 'Envoi…',
  'memories.empty': 'Aucune photo pour le moment. Soyez le premier à en ajouter une !',
  'memories.delete': 'Supprimer',
  'memories.delete_confirm': 'Supprimer cette photo ?',
  'memories.close': 'Fermer',
  'memories.upload_error': "Les photos n'ont pas pu être envoyées. Veuillez réessayer.",
  'memories.upload_failed': "Certaines photos n'ont pas pu être ajoutées : {names}",

  'settings.house_rules': 'Document de la maison',
  'settings.telescope': 'Téléscope',
  'settings.doc_placeholder': 'https://docs.google.com/…',
  'settings.doc_save': 'Enregistrer',
  'settings.doc_saved': 'Enregistré',
  'settings.doc_invalid': 'Saisissez une URL valide',
};

const TABLES: Record<Locale, Strings> = { en, fr };

export function t(key: string, params?: Record<string, string | number>): string {
  const table = TABLES[currentLocale];
  let s = table[key] ?? en[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return s;
}

export function formatAgo(seconds: number): string {
  if (seconds < 60) return t('time.seconds_ago', { n: seconds });
  if (seconds < 3600) return t('time.minutes_ago', { n: Math.floor(seconds / 60) });
  if (seconds < 86400) return t('time.hours_ago', { n: Math.floor(seconds / 3600) });
  return t('time.days_ago', { n: Math.floor(seconds / 86400) });
}
